import {
  AlertPermission,
  AssetPermission,
  OrganizationPermission,
  PeripheralPermission,
  ProfilePermission,
  Role,
  VehicleOrderPermission,
} from '@gm-commercial/profile-model';

import {
  CAPS_FLAG_NAME,
  CUSTOM_VIDEO_REQUEST_FLAG,
  DRIVERS_FLAG_NAME,
  FEEDBACK_LOOP_FLAG,
  HOME_CHARGE_SWITCHER_FLAG,
  MIGRATION_WELCOME_EXPERIENCE_FLAG,
  ORG_DETAILS_FLAG,
  PERIPHERALS_FLAG_NAME,
  SAFETY_PAGE_FLAG,
} from '~/common/models/featureFlags.model';
import { NOTIFICATION_ROUTE_AUTH } from '~/common/models/notification.model';
import {
  ACCOUNT_FLEET_PROGRAMS,
  ACCOUNT_ORG_DETAILS,
  ACCOUNT_PLAN_DETAIL_VIEW,
  ACCOUNT_PLAN_LIST_VIEW_PATH,
  ACCOUNT_PREFERENCES_LANGUAGE_REGION_PATH,
  ACCOUNT_PROFILE_PERSONAL_PATH,
  ACCOUNT_USER_LIST_PATH,
} from '~/common/models/pages/accountPages.model';
import {
  ADVISOR_PENDING_VEHICLES_LIST,
  ADVISOR_VEHICLE_LIST,
  FLEET_DRIVERS_LIST_DETAILS,
  FLEET_DRIVERS_LIST_DETAILS_CHARGE_SESSION,
  FLEET_NOTIFICATIONS_LIST,
  FLEET_PERIPHERALS_LIST,
  FLEET_VEHICLE_LIST_DETAILS,
  FLEET_VEHICLE_ORDERS_DETAILS,
  FLEET_VEHICLE_ORDERS_LIST,
  ORGANIZATIONS_CUSTOM_VIDEO_DETAILS,
  ORGANIZATIONS_CUSTOM_VIDEO_REQUEST_LIST,
} from '~/common/models/pages/fleetPages.model';
import {
  SETTINGS_NOTIFICATIONS_ACCOUNT,
  SETTINGS_NOTIFICATIONS_SAFETY_AND_SECURITY,
  SETTINGS_NOTIFICATIONS_VEHICLES,
  SETTINGS_VIEW_PATH,
} from '~/common/models/pages/settingsPages.model';
import { type RouteConfig, RouteName } from '~/common/models/route.model';
import { lazyWithReload } from '~/common/utils/lazyWithReload';
import { ORGANIZATIONS_ROUTES } from '~/routes/organizations/constants';

import { ACCOUNT_ROUTES } from './routes/account.config';
import { NOTIFICATION_SETTINGS_ROUTES } from './routes/notificationSettings.config';
import { SUPPORT_ROUTES } from './routes/support.config';

export type RouteParams = {
  organizationsId?: string;
  numVehicles?: string; //Temp Change: SDPCPLT-1848
  hubsId?: string;
  fleetsId?: string;
  assetsId?: string;
  usersId?: string;
  reportId?: string;
  assetType?: string;
  plansId?: string;
  viewType?: string;
  ordersId?: string;
  categoryId?: string;
  topicId?: string;
  productCode?: string;
  driversId?: string;
  chargeSessionId?: string;
  planId?: string;
  batchId?: string;
  eventId?: string;
  geofencesId?: string;
  tripId?: string;
  clipId?: string;
  tab?: string;
  partnerId?: string;
  notificationKey?: string;
  subscriptionId?: string;
  ruleId?: string;
};
//10/18/24: Below entry utilizes the MRT component for the User List page accessible via the Account Navigation.
const CSPlatformUserList = lazyWithReload(
  () => import('../../features/users/list/AccountUsersListView')
);
const VehicleDetailsFlagWrapper = lazyWithReload(
  () =>
    import(
      '~/routes/organizations/vehicles/details/components/VehicleDetailsFlagWrapper'
    )
);
const CSVehicleOrderDetails = lazyWithReload(
  () => import('~/features/orders/details/VehicleOrderDetailsView')
);

const UserTermsConditions = lazyWithReload(
  () => import('~/features/users/termsConditions/UserTermsConditions')
);

const GettingStartedView = lazyWithReload(
  () =>
    import('~/features/welcomeExperience/GettingStartedView/GettingStartedView')
);
const MigrationNotComplete = lazyWithReload(
  () =>
    import(
      '~/features/users/oviMigration/MigrationNotComplete/MigrationNotComplete'
    )
);

const TermsNotSigned = lazyWithReload(
  () => import('~/features/users/oviMigration/TermsNotSigned/TermsNotSigned')
);

const OnstarPlatformIsReady = lazyWithReload(
  () =>
    import(
      '~/features/users/oviMigration/OnstarPlatformIsReady/OnstarPlatformIsReady'
    )
);

const PlanDetails = lazyWithReload(
  () => import('~/features/plans/PlanDetails')
);
const AdvisorVehicleList = lazyWithReload(
  () => import('~/routes/legacy/vehicle/list/advisor/AdvisorVehicleList')
);
const PrivacyStatement = lazyWithReload(
  () => import('~/routes/privacyStatement/Privacy')
);
const UserTerms = lazyWithReload(
  () => import('~/routes/termsCondition/UserTerms')
);

const DevicesList = lazyWithReload(
  () => import('~/routes/organizations/devices/DevicesView')
);
const DriversList = lazyWithReload(
  () => import('~/routes/organizations/drivers/list/DriversList')
);
const DriversDetails = lazyWithReload(
  () => import('~/routes/organizations/drivers/details/DriversDetails')
);
const ChargeSessionDetails = lazyWithReload(
  () => import('~/routes/homeCharge/sessionDetails/ChargeSessionDetails')
);
const CSPlatformAccountPreferencesView = lazyWithReload(
  () =>
    import(
      '~/routes/account/preferences/components/CSPlatformAccountPreferencesView'
    )
);
const AccountProfileView = lazyWithReload(
  () => import('~/routes/account/profile/components/AccountProfileView')
);
const NotificationCenterView = lazyWithReload(
  () =>
    import(
      '~/routes/notifications/components/NotificationCenter/NotificationCenterView'
    )
);
const CSPlatformOrgDetailsView = lazyWithReload(
  () => import('~/features/organizations/details/OrganizationDetails')
);
const CSPlatformFleetPurchaseProgramsView = lazyWithReload(
  () => import('~/features/fleets/purchasePrograms/FleetPurchasePrograms')
);

const PlanManagementView = lazyWithReload(
  () => import('~/features/plans/planManagement/PlanManagementView')
);

const AdvisorPendingListView = lazyWithReload(
  () => import('~/features/assets/pending/AdvisorPendingListView')
);

const CSPlatformSettingsNotificationsSafetyAndSecurityView = lazyWithReload(
  () => import('~/routes/settings/NotificationSafetySecurityView')
);

const SettingsView = lazyWithReload(
  () => import('~/routes/settings/SettingsView')
);

const VehicleOrderListView = lazyWithReload(
  () => import('~/routes/organizations/orders/VehicleOrderListRouteView')
);

const NotificationVehiclesView = lazyWithReload(
  () => import('~/routes/settings/NotificationVehiclesView')
);
const NotificationsAccountsView = lazyWithReload(
  () => import('~/routes/settings/NotificationAccountsView')
);

const CustomVideoRequestView = lazyWithReload(
  () => import('~/routes/organizations/video/CustomVideoRequestView')
);

const CustomVideoDetailsView = lazyWithReload(
  () => import('~/routes/organizations/video/CustomVideoDetailsView')
);

const SafetyPage = lazyWithReload(() =>
  import('~/routes/organizations/safety/pages/SafetyPage').then((module) => ({
    default: module.SafetyPage,
  }))
);

const FeedbackList = lazyWithReload(
  () => import('~/routes/feedback/feedbackList/FeedbackList')
);

const VehicleEnrollment = lazyWithReload(
  () => import('~/features/catalog/__tools__/VehicleEnrollment')
);

export const APP_ROUTES_CONFIG_MAPPING = {
  ...ACCOUNT_ROUTES,
  ...NOTIFICATION_SETTINGS_ROUTES,
  ...SUPPORT_ROUTES,
  ...ORGANIZATIONS_ROUTES,
  [RouteName.ADVISOR_PENDING_VEHICLES]: {
    name: RouteName.ADVISOR_PENDING_VEHICLES,
    path: [`${ADVISOR_PENDING_VEHICLES_LIST}/:viewType`],
    authorizedPerms: [AssetPermission.READ],
    excludedRoles: [Role.OPERATOR],
    element: AdvisorPendingListView,
  },
  [RouteName.USER_TERMS_AND_CONDITIONS]: {
    name: RouteName.USER_TERMS_AND_CONDITIONS,
    path: '/users/:usersId/terms-and-conditions',
    authorizedPerms: [],
    element: UserTermsConditions,
  },
  [RouteName.GETTING_STARTED]: {
    name: RouteName.GETTING_STARTED,
    path: '/organizations/:organizationsId/getting-started',
    authorizedPerms: [],
    requiredFeatureFlags: [MIGRATION_WELCOME_EXPERIENCE_FLAG],
    element: GettingStartedView,
  },
  [RouteName.MIGRATION_NOT_COMPLETE]: {
    name: RouteName.MIGRATION_NOT_COMPLETE,
    path: '/migration-not-complete',
    authorizedPerms: [],
    element: MigrationNotComplete,
  },
  [RouteName.TERMS_NOT_SIGNED]: {
    name: RouteName.TERMS_NOT_SIGNED,
    path: '/terms-not-signed',
    authorizedPerms: [],
    element: TermsNotSigned,
  },
  [RouteName.ONSTAR_PLATFORM_IS_READY]: {
    name: RouteName.ONSTAR_PLATFORM_IS_READY,
    path: '/onstar-platform-is-ready',
    authorizedPerms: [],
    element: OnstarPlatformIsReady,
  },
  [RouteName.ADVISOR_VEHICLE_LIST]: {
    name: RouteName.ADVISOR_VEHICLE_LIST,
    path: ADVISOR_VEHICLE_LIST,
    authorizedRoles: [
      Role.SYSTEM_MANAGER,
      Role.SYSTEM_READER,
      Role.TECHNICAL_MANAGER,
    ],
    authorizedPerms: [AssetPermission.READ],
    element: AdvisorVehicleList,
  },
  [RouteName.PRIVACY]: {
    name: RouteName.PRIVACY,
    path: '/privacy-statement',
    authorizedPerms: [],
    element: PrivacyStatement,
  },
  [RouteName.USER_TERMS]: {
    name: RouteName.USER_TERMS,
    path: '/user-terms',
    authorizedPerms: [],
    element: UserTerms,
  },

  // NEW PAGES FOR CS PLATFORM
  [RouteName.CS_PLATFORM_ASSET_DETAILS]: {
    name: RouteName.CS_PLATFORM_ASSET_DETAILS,
    path: [
      FLEET_VEHICLE_LIST_DETAILS,
      `${FLEET_VEHICLE_LIST_DETAILS}/:viewType`,
    ],
    authorizedPerms: [AssetPermission.READ],
    excludedRoles: [Role.OPERATOR],
    element: VehicleDetailsFlagWrapper,
  },
  [RouteName.CS_PLATFORM_DRIVERS_DETAILS]: {
    name: RouteName.CS_PLATFORM_DRIVERS_DETAILS,
    path: [
      FLEET_DRIVERS_LIST_DETAILS,
      `${FLEET_DRIVERS_LIST_DETAILS}/:driversId`,
      '/organizations/:organizationsId/drivers/details/:driversId',
      '/organizations/:organizationsId/fleets/:fleetsId/drivers/details/:driversId',
    ],
    authorizedPerms: [PeripheralPermission.READ],
    authorizedRoles: [
      Role.SYSTEM_MANAGER,
      Role.ORG_MANAGER,
      Role.FLEET_MANAGER,
    ],
    secure: true,
    element: DriversDetails,
    requiredFeatureFlags: [DRIVERS_FLAG_NAME],
  },
  [RouteName.CS_PLATFORM_DRIVERS_DETAILS_CHARGE_SESSION]: {
    name: RouteName.CS_PLATFORM_DRIVERS_DETAILS_CHARGE_SESSION,
    path: [
      FLEET_DRIVERS_LIST_DETAILS_CHARGE_SESSION,
      `${FLEET_DRIVERS_LIST_DETAILS_CHARGE_SESSION}/:driversId/:chargeSessionId`,
      '/organizations/:organizationsId/drivers/details/:driversId/chargeSession/:chargeSessionId',
      '/organizations/:organizationsId/fleets/:fleetsId/drivers/details/:driversId/chargeSession/:chargeSessionId',
    ],
    authorizedPerms: [],
    authorizedRoles: [
      Role.SYSTEM_MANAGER,
      Role.ORG_MANAGER,
      Role.FLEET_MANAGER,
    ],
    secure: true,
    element: ChargeSessionDetails,
    requiredFeatureFlags: [HOME_CHARGE_SWITCHER_FLAG],
  },
  [RouteName.CS_PLATFORM_DRIVERS_LIST]: {
    name: RouteName.CS_PLATFORM_DRIVERS_LIST,
    path: [
      '/organizations/:organizationsId/drivers',
      '/organizations/:organizationsId/fleets/:fleetsId/drivers',
    ],
    authorizedPerms: [PeripheralPermission.READ],
    authorizedRoles: [
      Role.SYSTEM_MANAGER,
      Role.ORG_MANAGER,
      Role.FLEET_MANAGER,
    ],
    element: DriversList,
    requiredFeatureFlags: [DRIVERS_FLAG_NAME],
  },
  [RouteName.CS_PLATFORM_PERIPHERALS_LIST]: {
    name: RouteName.CS_PLATFORM_PERIPHERALS_LIST,
    path: [
      FLEET_PERIPHERALS_LIST,
      `${FLEET_PERIPHERALS_LIST}/:tab`,
      '/peripherals',
      '/peripherals/:tab',
    ],
    authorizedPerms: [PeripheralPermission.READ],
    authorizedRoles: [
      Role.SYSTEM_MANAGER,
      Role.ORG_MANAGER,
      Role.FLEET_MANAGER,
    ],
    element: DevicesList,
    requiredFeatureFlags: [PERIPHERALS_FLAG_NAME],
  },
  [RouteName.CS_PLATFORM_ORDER_LIST]: {
    name: RouteName.CS_PLATFORM_ORDER_LIST,
    path: FLEET_VEHICLE_ORDERS_LIST,
    authorizedPerms: [
      VehicleOrderPermission.READ,
      VehicleOrderPermission.UPDATE,
      VehicleOrderPermission.DELETE,
      VehicleOrderPermission.CREATE,
    ],
    element: VehicleOrderListView,
  },
  [RouteName.CS_PLATFORM_ORDER_DETAILS]: {
    name: RouteName.CS_PLATFORM_ORDER_DETAILS,
    path: FLEET_VEHICLE_ORDERS_DETAILS,
    authorizedPerms: [
      VehicleOrderPermission.READ,
      VehicleOrderPermission.UPDATE,
    ],
    element: CSVehicleOrderDetails,
  },
  [RouteName.PLAN_DETAIL_VIEW]: {
    name: RouteName.PLAN_DETAIL_VIEW,
    path: [`${ACCOUNT_PLAN_DETAIL_VIEW}/:productCode`],
    authorizedPerms: [AssetPermission.READ, OrganizationPermission.READ],
    authorizedRoles: [Role.ORG_MANAGER],
    element: PlanDetails,
  },
  [RouteName.CS_PLATFORM_SETTINGS]: {
    name: RouteName.CS_PLATFORM_SETTINGS,
    path: SETTINGS_NOTIFICATIONS_VEHICLES,
    ...NOTIFICATION_ROUTE_AUTH,
    authorizedPerms: [],
    element: NotificationVehiclesView,
  },
  [RouteName.CS_PLATFORM_ACCOUNTS_NOTIFICATIONS_SETTING]: {
    name: RouteName.CS_PLATFORM_ACCOUNTS_NOTIFICATIONS_SETTING,
    path: SETTINGS_NOTIFICATIONS_ACCOUNT,
    ...NOTIFICATION_ROUTE_AUTH,
    authorizedPerms: [],
    element: NotificationsAccountsView,
  },
  [RouteName.CS_PLATFORM_NOTIFICATION_CENTER_LIST]: {
    name: RouteName.CS_PLATFORM_NOTIFICATION_CENTER_LIST,
    path: FLEET_NOTIFICATIONS_LIST,
    ...NOTIFICATION_ROUTE_AUTH,
    authorizedPerms: [AlertPermission.READ],
    element: NotificationCenterView,
  },
  [RouteName.CS_PLATFORM_ACCOUNT_PREFERENCES]: {
    name: RouteName.CS_PLATFORM_ACCOUNT_PREFERENCES,
    path: 'account/profile/preferences',
    authorizedPerms: [ProfilePermission.READ, ProfilePermission.UPDATE],
    element: CSPlatformAccountPreferencesView,
  },
  [RouteName.ACCOUNT_PROFILE_PERSONAL]: {
    name: RouteName.CS_PLATFORM_SETTINGS,
    path: ACCOUNT_PROFILE_PERSONAL_PATH,
    authorizedPerms: [ProfilePermission.READ],
    element: AccountProfileView,
  },
  [RouteName.ACCOUNT_PREFERENCES_LANGUAGE_REGION]: {
    name: RouteName.CS_PLATFORM_SETTINGS,
    path: ACCOUNT_PREFERENCES_LANGUAGE_REGION_PATH,
    authorizedPerms: [],
    element: CSPlatformAccountPreferencesView,
  },

  [RouteName.CS_PLATFORM_ACCOUNT_ORG_DETAILS]: {
    name: RouteName.CS_PLATFORM_ACCOUNT_ORG_DETAILS,
    path: ACCOUNT_ORG_DETAILS,
    authorizedPerms: [],
    excludedRoles: [Role.OPERATOR, Role.VIEWER],
    element: CSPlatformOrgDetailsView,
    requiredFeatureFlags: [ORG_DETAILS_FLAG],
  },
  [RouteName.CS_PLATFORM_ACCOUNT_FLEET_PROGRAMS]: {
    name: RouteName.CS_PLATFORM_ACCOUNT_FLEET_PROGRAMS,
    path: ACCOUNT_FLEET_PROGRAMS,
    authorizedPerms: [],
    excludedRoles: [Role.OPERATOR],
    element: CSPlatformFleetPurchaseProgramsView,
    requiredFeatureFlags: [CAPS_FLAG_NAME],
  },
  [RouteName.CS_PLATFORM_SETTINGS_VIEW]: {
    name: RouteName.CS_PLATFORM_SETTINGS_VIEW,
    path: SETTINGS_VIEW_PATH,
    authorizedPerms: [],
    element: SettingsView,
  },
  [RouteName.CS_PLATFORM_USER_LIST]: {
    name: RouteName.CS_PLATFORM_USER_LIST,
    path: ACCOUNT_USER_LIST_PATH,
    authorizedPerms: [],
    excludedRoles: [Role.OPERATOR, Role.VIEWER],
    element: CSPlatformUserList,
  },
  [RouteName.PLANS_LIST_VIEW]: {
    name: RouteName.PLANS_LIST_VIEW,
    path: ACCOUNT_PLAN_LIST_VIEW_PATH,
    authorizedPerms: [AssetPermission.READ, OrganizationPermission.READ],
    authorizedRoles: [Role.ORG_MANAGER],
    element: PlanManagementView,
  },
  [RouteName.CS_PLATFORM_SAFETY_AND_SECURITY_NOTIFICATIONS_SETTINGS]: {
    name: RouteName.CS_PLATFORM_SAFETY_AND_SECURITY_NOTIFICATIONS_SETTINGS,
    path: SETTINGS_NOTIFICATIONS_SAFETY_AND_SECURITY,
    authorizedPerms: [],
    ...NOTIFICATION_ROUTE_AUTH,
    excludedRoles: [Role.OPERATOR],
    element: CSPlatformSettingsNotificationsSafetyAndSecurityView,
  },
  [RouteName.ORGANIZATIONS_SAFETY]: {
    name: RouteName.ORGANIZATIONS_SAFETY,
    path: '/organizations/:organizationsId/safety',
    authorizedPerms: [OrganizationPermission.READ],
    excludedRoles: [Role.OPERATOR],
    element: SafetyPage,
    requiredFeatureFlags: [SAFETY_PAGE_FLAG],
  },
  [RouteName.ORGANIZATIONS_SAFETY_EVENT_HISTORY]: {
    name: RouteName.ORGANIZATIONS_SAFETY_EVENT_HISTORY,
    path: '/organizations/:organizationsId/safety/event-history',
    authorizedPerms: [OrganizationPermission.READ],
    excludedRoles: [Role.OPERATOR],
    element: SafetyPage,
    requiredFeatureFlags: [SAFETY_PAGE_FLAG],
  },
  [RouteName.ORGANIZATIONS_SAFETY_LEADERBOARD]: {
    name: RouteName.ORGANIZATIONS_SAFETY_LEADERBOARD,
    path: '/organizations/:organizationsId/safety/leaderboard',
    authorizedPerms: [OrganizationPermission.READ],
    excludedRoles: [Role.OPERATOR],
    element: SafetyPage,
    requiredFeatureFlags: [SAFETY_PAGE_FLAG],
  },
  [RouteName.ORGANIZATIONS_SAFETY_EVENT_DETAIL]: {
    name: RouteName.ORGANIZATIONS_SAFETY_EVENT_DETAIL,
    path: '/organizations/:organizationsId/safety/events/:eventId',
    authorizedPerms: [OrganizationPermission.READ],
    excludedRoles: [Role.OPERATOR],
    element: lazyWithReload(() =>
      import('~/routes/organizations/safety/pages').then((module) => ({
        default: module.SafetyEventDetailRoute,
      }))
    ),
    requiredFeatureFlags: [SAFETY_PAGE_FLAG],
  },
  [RouteName.ORGANIZATIONS_SAFETY_DRIVER_PERFORMANCE]: {
    name: RouteName.ORGANIZATIONS_SAFETY_DRIVER_PERFORMANCE,
    path: '/organizations/:organizationsId/safety/drivers/:driversId',
    authorizedPerms: [OrganizationPermission.READ],
    excludedRoles: [Role.OPERATOR],
    element: lazyWithReload(() =>
      import('~/routes/organizations/safety/pages').then((module) => ({
        default: module.DriverPerformanceRoute,
      }))
    ),
    requiredFeatureFlags: [SAFETY_PAGE_FLAG],
  },
  [RouteName.ORGANIZATIONS_CUSTOM_VIDEO_REQUEST]: {
    name: RouteName.ORGANIZATIONS_CUSTOM_VIDEO_REQUEST,
    path: ORGANIZATIONS_CUSTOM_VIDEO_REQUEST_LIST,
    authorizedPerms: [],
    element: CustomVideoRequestView,
    requiredFeatureFlags: [CUSTOM_VIDEO_REQUEST_FLAG],
  },
  [RouteName.CS_PLATFORM_FEEDBACK]: {
    name: RouteName.CS_PLATFORM_FEEDBACK,
    path: '/feedback',
    authorizedPerms: [],
    authorizedRoles: [Role.SYSTEM_MANAGER],
    excludedRoles: [Role.ORG_MANAGER, Role.FLEET_MANAGER],
    element: FeedbackList,
    requiredFeatureFlags: [FEEDBACK_LOOP_FLAG],
  },
  [RouteName.ORGANIZATIONS_CUSTOM_VIDEO_DETAILS]: {
    name: RouteName.ORGANIZATIONS_CUSTOM_VIDEO_DETAILS,
    path: ORGANIZATIONS_CUSTOM_VIDEO_DETAILS,
    authorizedPerms: [],
    element: CustomVideoDetailsView,
    requiredFeatureFlags: [CUSTOM_VIDEO_REQUEST_FLAG],
  },
  [RouteName.BPS_VEHICLE_ENROLLMENT]: {
    name: RouteName.BPS_VEHICLE_ENROLLMENT,
    path: '/organizations/bps/vehicle/enrollment',
    authorizedPerms: [],
    element: VehicleEnrollment,
    authorizedRoles: [Role.SYSTEM_MANAGER, Role.TECHNICAL_MANAGER],
  },
};

export const SecureRoutes = Object.values(APP_ROUTES_CONFIG_MAPPING).reduce(
  (acc, route) => {
    if (Array.isArray(route.path)) {
      for (const path of route.path) {
        acc.push({ ...route, path } as RouteConfig);
      }
    } else {
      acc.push(route as RouteConfig);
    }
    return acc;
  },
  [] as RouteConfig[]
);
